<script setup lang="ts">
import { computed, nextTick, ref, toRef } from 'vue';
import { ContentsContext, Feature, PageName } from '@/api/tracking';
import AdpDocumentActionBar from '@/components/adp-document/actions/adp-document-action-bar.vue';
import AdpDocumentBasicInfo from '@/components/common/adp-document/basic-info.vue';
import CommentCreateModal from '@/components/modals/comment-create-modal.vue';
import DialogGroupMark from '@/components/modals/dialog-group-mark.vue';
import MarkModal from '@/components/modals/mark-modal.vue';
import { ActionModal, AdpDocument, Group, Mention } from '@/types';
import { useComment } from '@/utils/actions/comment';
import { useGroupMark } from '@/utils/actions/group-mark';
import { useMark } from '@/utils/actions/mark';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import { getSiteType } from '@/utils/adpDocument';
import { useAdpDocumentMarkPresentation } from '@/utils/composables/useAdpDocumentMarkPresentation';
import {
  copyLinkOptionAction,
  useCopyDocumentLink,
} from '@/utils/composables/useCopyDocumentLink';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useEmitter, useStore } from '@/utils/vue';

interface Props {
  relatedArticle: AdpDocument;
  pageName: PageName;
  rankInWholeFeed: number;
  relatedQuery?: string;
  requestId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  relatedQuery: '',
  requestId: '',
});

const store = useStore();
const emitter = useEmitter();

const relatedArticleRef = toRef(props, 'relatedArticle');
const { isMarked, markedUsers, markedGroups, commentedUsers } =
  useAdpDocumentMarkPresentation(relatedArticleRef);
const { copyLink } = useCopyDocumentLink();
const optionActions = [copyLinkOptionAction];
const handleOptionAction = () => {
  if (relatedArticleRef.value) copyLink(relatedArticleRef.value);
};
const { getCurrentPathForTracking } = useCurrentPathForTracking();
const actionModal = ref<ActionModal>(undefined);

const { createMark, removeMark } = useMark({
  markSource: 'article',
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: 'related_contents' as Feature,
  },
  rankInWholeFeed: props.rankInWholeFeed,
  contentsContext: {
    event_name: 'related_contents',
    request_id: props.requestId,
    search_query: props.relatedQuery,
  } as ContentsContext,
});

const { createGroupMark } = useGroupMark({
  markSource: 'article',
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: 'related_contents' as Feature,
  },
  rankInWholeFeed: props.rankInWholeFeed,
  contentsContext: {
    event_name: 'related_contents',
    request_id: props.requestId,
    search_query: props.relatedQuery,
  } as ContentsContext,
});

const { createComment } = useComment({
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: 'related_contents' as Feature,
  },
  rankInWholeFeed: props.rankInWholeFeed,
  contentsContext: {
    event_name: 'related_contents',
    request_id: props.requestId,
    search_query: props.relatedQuery,
  } as ContentsContext,
});

const isMarkingArticle = ref(false);
const isPostingComment = ref(false);
const userInfo = computed(() => store.state.userInfo.userInfo);
const hasMyMark = computed(() =>
  props.relatedArticle.marks.find(m => m.user_id === userInfo.value?.id),
);
const markArticle = async (markProps?: { tags?: string[]; memo?: string }) => {
  if (isMarkingArticle.value || !userInfo.value) return;
  isMarkingArticle.value = true;
  try {
    if (hasMyMark.value) {
      await removeMark({
        adpDocument: props.relatedArticle,
        userInfo: userInfo.value,
      });
    } else {
      await createMark({
        adpDocument: props.relatedArticle,
        userInfo: userInfo.value,
        tags: markProps?.tags,
        memo: markProps?.memo,
      });
      closeActionModal();
    }
  } finally {
    await nextTick();
    isMarkingArticle.value = false;
  }
};

const groupMarkArticle = async ({
  group,
  tags,
  memo,
}: {
  group: Group;
  tags?: string[];
  memo?: string;
}) => {
  await createGroupMark({
    adpDocument: props.relatedArticle,
    group: group,
    tags: tags,
    memo: memo,
  });
  closeActionModal();
  emitter.emit('article-updated', props.relatedArticle);
};

const commentArticle = async ({
  group,
  content,
  mention,
}: {
  group: Group;
  content: string;
  mention?: Mention;
}) => {
  try {
    isPostingComment.value = true;
    const createdComment = await createComment({
      groupId: group.id,
      adpDocument: props.relatedArticle,
      content,
      mention,
    });
    emitter.emit('article-updated', props.relatedArticle);
    emitter.emit('comment-created', {
      ...createdComment,
      article: props.relatedArticle,
    });
    closeActionModal();
  } finally {
    isPostingComment.value = false;
  }
};

const showCreateCommentModal = () => {
  actionModal.value = 'comment';
};
const showGroupMarkModal = () => {
  actionModal.value = 'group_mark';
};
const showMarkModal = () => {
  actionModal.value = 'mark';
};

const closeActionModal = () => {
  actionModal.value = undefined;
};

const teamInfo = computed(() => store.state.teamInfo.teamInfo);
const enableAllianceMedia = computed(
  () => teamInfo.value?.enable_alliance_media ?? false,
);

const createSendViewProps = (article: AdpDocument) => ({
  adpDocument: article,
  rankInWholeFeed: props.rankInWholeFeed,
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: 'related_contents' as Feature,
  },
  siteType: getSiteType(article),
  enableAllianceMedia: enableAllianceMedia.value,
  contentsContext: {
    event_name: 'related_contents',
    request_id: props.requestId,
    search_query: props.relatedQuery,
  } as ContentsContext,
});

const articleClicked = async (article: AdpDocument) => {
  await articleClickedEvent(createSendViewProps(article), article);
};

const articleContextMenuClicked = async (
  article: AdpDocument,
  event: Event,
) => {
  articleContextMenuClickedEvent(createSendViewProps(article), event, article);
};
</script>

<template>
  <div class="article-container">
    <AdpDocumentBasicInfo
      :adp-document="relatedArticle"
      :is-related-article="true"
      size="m"
      @article-link-clicked="articleClicked(relatedArticle)"
      @article-link-context-menu="
        articleContextMenuClicked(relatedArticle, $event)
      "
    ></AdpDocumentBasicInfo>
    <AdpDocumentActionBar
      :adp-document="relatedArticle"
      :is-marked="isMarked"
      :marked-users="markedUsers"
      :marked-groups="markedGroups"
      :commented-users="commentedUsers"
      :option-actions="optionActions"
      :mark-handler="markArticle"
      @option-action="handleOptionAction"
      @handle-popup-menu-click="showMarkModal"
      @handle-comment-click="showCreateCommentModal"
      @handle-group-mark-click="showGroupMarkModal"
    ></AdpDocumentActionBar>
    <MarkModal
      v-if="actionModal === 'mark'"
      :adp-document="relatedArticle"
      :page-name="pageName"
      feature="related_contents"
      @submit="markArticle"
      @close="closeActionModal"
    ></MarkModal>
    <DialogGroupMark
      v-if="actionModal === 'group_mark'"
      :adp-document="relatedArticle"
      :page-name="pageName"
      feature="related_contents"
      @submit="groupMarkArticle"
      @close="closeActionModal"
    ></DialogGroupMark>
    <CommentCreateModal
      v-else-if="actionModal === 'comment'"
      :adp-document="relatedArticle"
      :page-name="pageName"
      feature="related_contents"
      :is-posting-comment="isPostingComment"
      @submit="commentArticle"
      @close="closeActionModal"
    ></CommentCreateModal>
  </div>
</template>

<style lang="scss" scoped>
.article-container {
  border-radius: 4px;
  border: 1px solid $color-border;
  background: #fff;
  display: flex;
  flex-direction: column;
  padding: 8px;
  margin-top: 12px;
}
</style>
